/**
 * MessageInputBar.js
 * Talkroute
 *
 * Created by Vel Genov on 5/1/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import { connect } from "react-redux";
import TextareaAutosize from "react-textarea-autosize";
import { Button, Label, Grid } from "semantic-ui-react";

import { sendMessage } from "../../actions/conversationActions";
import uiString from "../../constants/uiString";
import {
  NEW_CONVERSATION_ID,
  ConversationData,
  WELCOME_CONVERSATION,
} from "../../services/dataClasses/ConversationData";
import countBytesUTF8 from "../../helpers/stringHelper";
import PictureInput from "./PictureInput";
import dataStateEnum from "../../constants/dataStateConstants";
import SendSvg from "@resources/icons/message-input-box-send.svg";
import { domIds } from "@constants/testConstants";
import Tooltip from '@components/PresentationComponents/FormComponents/Tooltip';
import keyConstants from "@constants/keyConstants";
import { getTextMessageAllowedForNumber, TalkroutePhoneNumber } from "@services/dataClasses/TalkroutePhoneNumber";

// The style of this element
const messageInputBarStyle = {
  borderTop: "1px solid var(--color-dark-grey)",
  width: "100%",
  backgroundColor: "var(--color-background-grey)",
};

// The maximum number of characters allowed in a message
const maxCharactersInMessage = 1000;

// The duration of the error message label in seconds
const errorMessageDuration = 2000;

interface IMessageInputBarProps {
  conversationId?: string;
  draftMessageFiles: string[];
  conversationDraft: ConversationData;
  conversations: any[];
  outgoingPhoneNumbers: TalkroutePhoneNumber[];
  sendingState: string;
  openFileTrigger: any;
  isLoading: boolean;
  isPreferencesVisible: boolean;
  isDialogOpened: boolean;
  sendMessage: (props: {
    conversationId?: string;
    messageBody: string;
    files: any[];
  }) => any;
  spellcheck: boolean;
}

/**
 * Display a an imput bar that allows the user to type in a message
 *
 */
class MessageInputBar extends React.Component<IMessageInputBarProps, any> {
  public textArea;

  static defaultProps = {
    sendingState: dataStateEnum.READY,
    isLoading: false,
    conversations: [],
    outgoingPhoneNumbers: [],
  };

  /**
   * A component that displays the message length
   *
   * @param messageLength the message length to display
   */
  messageLengthDisplay = (messageLength) =>
    `${messageLength}/${maxCharactersInMessage}`;

  /**
   * Creates an action to send a message
   */
  onSendMessage = () => {
    const {
      sendMessage: sendMessageLocal,
      conversationId,
      conversationDraft,
      draftMessageFiles,
    } = this.props;
    const { textFieldValue } = this.state;

    if (conversationId === NEW_CONVERSATION_ID) {
      const { contactNumber } = conversationDraft;

      if (contactNumber !== null) {
        sendMessageLocal({
          conversationId,
          messageBody: textFieldValue,
          files: draftMessageFiles,
        });
        this.clearTextField();
      } else {
        this.setErrorMessageLabel(uiString.ENTER_PHONE_NUMBER);
        setTimeout(() => {
          this.clearErrorMessageLabel();
        }, errorMessageDuration);
      }
      return;
    }
    sendMessageLocal({
      conversationId,
      messageBody: textFieldValue,
      files: draftMessageFiles,
    });
    this.clearTextField();
  };

  /**
   * Gives the focus to the input text field
   */
  focusTextField = () => {
    this.textArea.focus();
  };

  /**
   * Clears the input text field
   */
  clearTextField = () => {
    this.setState({ textFieldValue: "" });
  };

  /**
   * Enables the message send buttion
   */
  enableSendButton = () => {
    this.setState({ sendButtonDisabled: false });
  };

  /**
   * Disables the message send button
   */
  disableSendButton = () => {
    this.setState({ sendButtonDisabled: true });
  };

  /**
   * Set the text for the error message label
   * @param errorMessage the message to show
   */
  setErrorMessageLabel = (errorMessage) => {
    this.setState({ errorMessageLabel: errorMessage });
  };

  /**
   * Clear the error message label
   */
  clearErrorMessageLabel = () => {
    this.setState({ errorMessageLabel: "" });
  };

  /**
   * Sets the input field text
   */
  setInputFieldText = (text) => {
    this.setState({ textFieldValue: text });
  };

  /**
   * Display the error message label using the text in state.errorMessageLabel
   * Hide if the text is empty
   */
  displayErrorMessageLabel = () => {
    const { errorMessageLabel } = this.state;
    if (errorMessageLabel) {
      return (
        <Label color="red" pointing="below">
          {errorMessageLabel}
        </Label>
      );
    }

    return null;
  };

  getIsEditable = () => {
    const { conversations, conversationId, outgoingPhoneNumbers } = this.props;

    if (conversationId === NEW_CONVERSATION_ID) {
      return true;
    }

    if (conversationId === "Talkroute") {
      return true;
    }

    let currentPhoneNumber = ""; 
  
    if (conversations && conversationId) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === conversationId
      );
      if (currentConversation) {
        currentPhoneNumber = currentConversation.talkrouteNumber; 
      }
    }
  
    return getTextMessageAllowedForNumber(currentPhoneNumber, outgoingPhoneNumbers);
  }

  /**
   * Message inputbox's key down event handler
   */
  onKeyDown = (e) => {
    // If enter key is presssed, not shift key and send button is enabled, sends the message
    const { sendButtonDisabled } = this.state;

    if (e.keyCode === keyConstants.CODE_ENTER && e.shiftKey === false) {
      e.preventDefault();
      if (!sendButtonDisabled) this.onSendMessage();
    }
  };

  onGlobalKeydown = (event: KeyboardEvent) => {
    // don't focus if you are writing in CONVERSATIONS_TO_PHONE_NUMBER_INPUT
    if (this.props.isDialogOpened) return;
    if (this.props.isPreferencesVisible || event.ctrlKey || event.metaKey) return;
    if ((event.target as HTMLElement).id === domIds.CONVERSATIONS_TO_PHONE_NUMBER_INPUT) return
    if (/^[\x00-\x7F]$/.test(event.key) && this.textArea) {
      this.focusTextField();
    }
  }

  constructor(props) {
    super(props);
    this.state = {
      textFieldValue: "",
      sendButtonDisabled: true,
      errorMessageLabel: "",
    };
  }

  componentDidMount() {
    window.addEventListener("keydown", this.onGlobalKeydown);
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.onGlobalKeydown);
  }

  componentDidUpdate(prevProps, prevState) {
    const { textFieldValue: prevTextFieldValue } = prevState;
    const { textFieldValue, sendButtonDisabled } = this.state;

    const {
      conversationId: prevConversationId,
      draftMessageFiles: prevDraftMessageFiles,
      sendingState: prevSendingState,
    } = prevProps;
    const {
      conversationId,
      draftMessageFiles,
      sendingState,
      conversationDraft,
    } = this.props;

    if (
      prevTextFieldValue !== textFieldValue ||
      prevDraftMessageFiles !== draftMessageFiles
    ) {
      // Check for null or empty or all white space in the text field and disable the send button if that's the case
      // Enable the send button if there is text or an image to send and we are ready to send
      if ((textFieldValue && textFieldValue.trim()) || draftMessageFiles) {
        this.enableSendButton();
      } else {
        this.disableSendButton();
      }
    }

    if (
      prevSendingState !== sendingState &&
      sendingState !== dataStateEnum.READY
    ) {
      this.disableSendButton();
    }

    if (countBytesUTF8(textFieldValue) > maxCharactersInMessage) {
      this.setInputFieldText(textFieldValue.substring(0, maxCharactersInMessage));
    }

    if (conversationId !== prevConversationId) {
      this.clearTextField();
    }

    // If user selects other conversaton, gives focus to the message input field
    if (
      prevConversationId !== conversationId &&
      (conversationId !== NEW_CONVERSATION_ID ||
        conversationDraft.contactNumber)
    ) {
      this.focusTextField();
    }
    // If user enters the phone number of new conversation, gives focus to the message input field
    if (
      prevConversationId === conversationId &&
      conversationId === NEW_CONVERSATION_ID &&
      conversationDraft.contactNumber
    ) {
      this.focusTextField();
    }

    if (
      conversationId === WELCOME_CONVERSATION &&
      sendButtonDisabled === false
    ) {
      this.disableSendButton();
    }
  }

  render() {
    const { textFieldValue, sendButtonDisabled } = this.state;
    const { draftMessageFiles, openFileTrigger, isLoading } = this.props;
    const isInputDisabled = !this.getIsEditable();
    const disabledTextFieldValue = "View-Only";

    return (
      <Grid
        style={{
          margin: "0px",
          padding: "0.5em 0.5em 0 0.5em",
          ...messageInputBarStyle,
        }}
      >
        {this.displayErrorMessageLabel()}
        {/* Picture attachment */}
        {draftMessageFiles || isLoading ? (
          <Grid.Row style={{ padding: "14px 8px 0px 8px" }}>
            <PictureInput pictures={draftMessageFiles} isLoading={isLoading} />
          </Grid.Row>
        ) : null}

        <Grid.Row style={{ padding: "8px 12px 0px 12px" }}>
          <div 
            className="message-input-container"
            style={{
              backgroundColor: isInputDisabled ? 'var(--color-light-grey)' : 'initial'
            }}
          >
            {isInputDisabled ? (
              <div className="message-input-attach">{openFileTrigger}</div>
            ) : (
              <Tooltip placement="top" title="Attach File">
                <div className="message-input-attach">{openFileTrigger}</div>
              </Tooltip>
            )}
            <div className="message-input-box">
              {/* Input text box */}
              <TextareaAutosize
                className="message-input text-message-input"
                minRows={1}
                maxRows={5}
                style={{
                  backgroundColor: isInputDisabled ? 'var(--color-light-grey)' : 'initial'
                }}
                value={isInputDisabled ? disabledTextFieldValue : textFieldValue}
                onChange={(e) => {
                  this.setState({ textFieldValue: e.target.value });
                }}
                inputRef={(textArea) => {
                  this.textArea = textArea;
                }}
                spellCheck={this.props.spellcheck}
                onKeyDown={this.onKeyDown}
                autoFocus
                disabled={isInputDisabled}
              />
            </div>
            <div className="message-input-send">
              {/* Send button */}
              <Tooltip placement="top" title="Send Message">
                <Button
                  basic
                  icon
                  compact
                  style={{
                    boxShadow: "none",
                    paddingLeft: "0px",
                    paddingRight: "0px",
                  }}
                  disabled={sendButtonDisabled || isInputDisabled}
                  onClick={() => this.onSendMessage()}
                >
                  <SendSvg />
                </Button>
              </Tooltip>
            </div>
          </div>
        </Grid.Row>
        <Grid.Row style={{ padding: "0px 12px" }}>
          <Grid.Column
            width={4}
            style={{ padding: "0px", margin: "auto" }}
            textAlign="center"
            className="text-tiny-light"
          >
            {uiString.ENTER_TO_SEND}
          </Grid.Column>
          <Grid.Column
            width={9}
            style={{ padding: "0px", margin: "auto" }}
            textAlign="left"
            className="text-tiny-light"
          >
            {uiString.SHIFT_ENTER_INSERT_NEW_LINE}
          </Grid.Column>
          <Grid.Column
            width={3}
            style={{ padding: "0px", margin: "auto" }}
            textAlign="right"
            className="text-small"
          >
            {this.messageLengthDisplay(countBytesUTF8(textFieldValue))}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state) => ({
  draftMessageFiles: state.textMessageDraft.files,
  sendingState: state.textMessageDraft.sendingState,
  isPreferencesVisible: state.preferences.isPreferencesVisible,
  isDialogOpened: state.appReducer.isDialogOpened,
  conversations: state.conversations.data,
  outgoingPhoneNumbers: state.preferences.outgoingTextPhoneNumbers,
  spellcheck: state.preferences.autoSpellCheck,
});

export default connect(mapStateToProps, { sendMessage })(MessageInputBar);
